/**
 * Phase C · PR 5 — Player Re-ID Stitch Button + Confidence Badge.
 *
 * Drops into the clip action-row inside SequenceLab.jsx next to the
 * MP4/CSV/Re-Render buttons. Three states:
 *
 *   1. NEVER RUN     → small "🧬 Stitch Tracks" button (click to kick).
 *   2. BUSY          → spinner button.
 *   3. RESULT KNOWN  → button shows current badge (high/medium/low
 *                      confidence) + a popover with the per-group
 *                      breakdown. Clicking re-runs the stitcher.
 *
 * The persistence of `stitch_summary` on the clip document means the
 * coach sees the same badge color on every page-load without paying
 * the ~400ms compute cost a second time. Hovering the badge surfaces
 * each merged group's confidence + raw → stitched id mapping in a
 * compact tooltip.
 */
import React, { useState } from "react";
import { Loader2, Users, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { stitchClipTracks } from "@/lib/api";


const CONFIDENCE_STYLES = {
  high:   "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
  medium: "bg-amber-300/15 text-amber-200 border-amber-300/40",
  low:    "bg-rose-400/15 text-rose-300 border-rose-400/40",
};


export default function ReidStitchButton({ clip, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ pct: 0, message: "" });
  const summary = clip.stitch_summary || null;

  const onStitch = async () => {
    setBusy(true);
    setProgress({ pct: 0, message: "queued" });
    const tid = toast.loading("🧬 Stitching tracklets · queued…");
    try {
      const res = await stitchClipTracks(clip.id, {
        onProgress: ({ progress_pct, message }) => {
          setProgress({ pct: progress_pct, message });
          toast.loading(
            `🧬 Stitching tracklets · ${progress_pct}% · ${message}`,
            { id: tid },
          );
        },
      });
      const s = res.stitch_summary;
      if (!s) {
        toast.error("Stitch finished without a summary payload", { id: tid });
        return;
      }
      toast.success(
        `🧬 Stitched ${s.n_raw_tracklets} → ${s.n_stitched_players} players · `
        + `${s.n_merged_groups} merges · ${s.confidence_label} confidence`,
        { id: tid },
      );
      onUpdate?.();
    } catch (err) {
      toast.error(err?.detail || err?.message || "Stitch failed", { id: tid });
    } finally {
      setBusy(false);
      setProgress({ pct: 0, message: "" });
    }
  };

  // First-time call — bare action button with inline progress while busy.
  if (!summary) {
    return (
      <Button
        data-testid={`reid-stitch-btn-${clip.id}`}
        data-job-progress={busy ? progress.pct : undefined}
        data-job-message={busy ? progress.message : undefined}
        size="sm"
        variant="ghost"
        onClick={onStitch}
        disabled={busy}
        className="h-7 px-2 hover:bg-accent hover:text-primary font-mono text-[10px] relative overflow-hidden"
        title={
          busy
            ? `Stitching · ${progress.pct}% · ${progress.message}`
            : "Run Hungarian + Kalman tracklet stitcher to merge fractured player IDs across occlusions"
        }
      >
        {busy && (
          <span
            data-testid={`reid-stitch-progress-${clip.id}`}
            className="absolute left-0 top-0 h-full bg-primary/15 transition-[width] duration-300"
            style={{ width: `${progress.pct}%` }}
          />
        )}
        <span className="relative flex items-center">
          {busy
            ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            : <Users className="w-3.5 h-3.5 mr-1" />}
          {busy ? `${progress.pct}% · ${progress.message || "starting"}` : "Stitch"}
        </span>
      </Button>
    );
  }

  // Already stitched — show badge + popover with breakdown.
  const styleClass = CONFIDENCE_STYLES[summary.confidence_label] || CONFIDENCE_STYLES.low;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          data-testid={`reid-stitch-badge-${clip.id}`}
          data-confidence={summary.confidence_label}
          size="sm"
          variant="outline"
          className={`h-7 px-2 border font-mono text-[10px] uppercase tracking-widest ${styleClass}`}
          title={
            `Re-ID stitcher · ${summary.n_raw_tracklets} raw → ${summary.n_stitched_players} stitched · `
            + `${summary.n_merged_groups} merges · mean confidence ${summary.mean_reid_confidence}`
          }
        >
          <Users className="w-3.5 h-3.5 mr-1" />
          🧬 {summary.n_stitched_players} · {summary.confidence_label}
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`reid-stitch-popover-${clip.id}`}
        align="end"
        className="w-80 p-4 bg-card border-primary/30"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold uppercase tracking-widest text-[10px] text-primary">
            Re-ID Tracklet Stitch
          </div>
          <Badge className={`border font-mono text-[9px] ${styleClass}`}>
            {summary.confidence_label}
          </Badge>
        </div>
        <div className="text-[10px] font-mono text-muted-foreground mb-3 leading-relaxed">
          <div>raw tracklets: <span className="text-foreground tabular-nums">{summary.n_raw_tracklets}</span></div>
          <div>stitched players: <span className="text-foreground tabular-nums">{summary.n_stitched_players}</span></div>
          <div>merges performed: <span className="text-foreground tabular-nums">{summary.n_merged_groups}</span></div>
          <div>global confidence: <span className="text-foreground tabular-nums">{(summary.mean_reid_confidence * 100).toFixed(0)}%</span></div>
          {summary.mean_merge_confidence != null && (
            <div>merge-only confidence: <span className="text-foreground tabular-nums">{(summary.mean_merge_confidence * 100).toFixed(0)}%</span></div>
          )}
        </div>
        {summary.stitched_tracklets?.filter((s) => s.member_pids.length > 1).length > 0 ? (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            <div className="font-display font-bold uppercase tracking-widest text-[9px] text-muted-foreground mb-1">
              Merged Chains
            </div>
            {summary.stitched_tracklets
              .filter((s) => s.member_pids.length > 1)
              .map((s) => (
                <div
                  key={s.stitched_id}
                  data-testid={`reid-merged-group-${s.stitched_id}`}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-background/40 border border-border/40"
                >
                  <div className="font-mono text-[10px] tabular-nums">
                    #{s.member_pids.join(" → ")}
                  </div>
                  <Badge
                    className={`border font-mono text-[9px] ${CONFIDENCE_STYLES[s.confidence_label] || CONFIDENCE_STYLES.low}`}
                  >
                    {(s.reid_confidence * 100).toFixed(0)}%
                  </Badge>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-[10px] font-mono text-muted-foreground italic">
            No merges needed — every tracklet was already a distinct player.
          </div>
        )}
        <Button
          data-testid={`reid-restitch-btn-${clip.id}`}
          data-job-progress={busy ? progress.pct : undefined}
          size="sm"
          variant="ghost"
          onClick={onStitch}
          disabled={busy}
          className="mt-3 w-full h-7 hover:bg-accent hover:text-primary font-display font-bold uppercase tracking-widest text-[9px] relative overflow-hidden"
        >
          {busy && (
            <span
              data-testid={`reid-restitch-progress-${clip.id}`}
              className="absolute left-0 top-0 h-full bg-primary/15 transition-[width] duration-300"
              style={{ width: `${progress.pct}%` }}
            />
          )}
          <span className="relative flex items-center">
            {busy
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {progress.pct}% · {progress.message || "starting"}</>
              : <>Re-stitch</>}
          </span>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
